import { DateTime } from 'luxon';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, Col, DropdownButton, Form, Row } from 'react-bootstrap';
import DropdownItem from 'react-bootstrap/esm/DropdownItem';
import { connect, ConnectedProps } from 'react-redux';
import { toast } from 'react-toastify';
import apis from '../../apis';
import {
  DateTimePicker,
  EditableProperties,
  EditableTableDiv,
  HeaderDisplayKeysWithType,
} from '../../components';
import { ClassSem, WrapClassBaseLayout } from '../../layouts';
import { PHISight, SimpleStudent } from '../../model';
import { ErrorDispatches } from '../../redux/Dispatches';
import { ApplicationState } from '../../redux/States';
import { Nullable } from '../../types';
import { NoticeSightTableHeader } from '../TableHeaders/NoticeSightHeader';
import DatePicker from 'react-multi-date-picker';

type StudentWithEditable = SimpleStudent &
  EditableProperties &
  Nullable<PHISight>;

const modalDisplayHeader: HeaderDisplayKeysWithType<StudentWithEditable>[] = [
  { property: 'seat', display: '座號' },
  { property: 'name', display: '學生' },
  ...(NoticeSightTableHeader as HeaderDisplayKeysWithType<StudentWithEditable>[]),
];

const mapState = (state: ApplicationState, ownProps: ClassSem) => ({
  ...ownProps,
});
const mapDispatch = { ...ErrorDispatches };
const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector>;

const dropdownItems = {
  '1': '顯示全部',
  '2': '顯示視力不良者',
  '3': '顯示視力正常者',
} as { [k: string]: string };

const sightPDFPage: FunctionComponent<Props> = ({
  classId,
  className,
  year,
  sem,
  catchErrorForModal,
}) => {
  const [originalStudents, setOriginalStudent] = useState<
    StudentWithEditable[]
  >([]);
  const [students, setStudent] = useState<StudentWithEditable[]>([]);
  const [printSightHistories, setPrintSightHistories] = useState(false);
  const [printCurrentSem, setPrintCurrentSem] = useState(false);
  const [deadline, setDeadline] = useState<DateTime>();
  const [selectShow, setSelectShow] = useState<keyof typeof dropdownItems>('1');
  useEffect(() => {
    refreshStudents();
  }, [classId, year, sem]);

  const filterStudents = (
    students: StudentWithEditable[],
    type: keyof typeof dropdownItems
  ) => {
    switch (type) {
      case '2':
        return students.filter(s => s.measureResult !== '1');
      case '3':
        return students.filter(s => s.measureResult === '1');
      default:
        return students;
    }
  };
  function refreshStudents() {
    if (classId && year && sem) {
      apis
        .getClassStudentSight(classId, year, sem)
        .then((ss) => {
          const students = ss.map(({ sight, ...s }) => ({
            ...sight,
            ...s,
            selected: true,
          }));

          setOriginalStudent(students);
          setStudent(filterStudents(students, selectShow));
        })
        .catch(catchErrorForModal);
    } else {
      setOriginalStudent([]);
      setStudent([]);
    }
  }

  if (!classId) return <></>;

  const handleNoticePrint = async () => {
    let pids = students
      .filter((s) => s.selected)
      .map((s) => s.pid);

    pids =
      pids.length === 0
        ? students.map((s) => s.pid)
        : pids;

    try {
      const blob = await toast.promise(
        apis.downloadSightPDF(
          classId,
          pids,
          year,
          sem,
          printCurrentSem,
          printSightHistories,
          deadline
        ),
        {
          pending: '通知單產生中...',
          success: '通知單產生成功！',
          error: '通知單產生失敗！請查看錯誤資訊。',
        }
      );

      const pdfBlob = new Blob(
        [blob],
        {
          type: 'application/pdf',
        }
      );

      const pdfUrl =
        window.URL.createObjectURL(pdfBlob);

      // API 完成後才開啟 PDF
      const printWindow = window.open(
        pdfUrl,
        '_blank'
      );

      if (!printWindow) {
        // 如果瀏覽器阻擋新視窗，就直接在目前頁面開 PDF
        window.location.href = pdfUrl;
        return;
      }

      // 過一段時間再釋放 URL
      setTimeout(() => {
        window.URL.revokeObjectURL(pdfUrl);
      }, 60000);
    } catch (error) {
      catchErrorForModal(error);
    }
  };

  return (
    <>
      <Row className="mb-2">
        <DropdownButton
          className="mr-4"
          title={dropdownItems[selectShow]}
          onSelect={(k) => {
            if (!k || !dropdownItems[k]) return;

            const type = k as keyof typeof dropdownItems;

            setSelectShow(type);
            setStudent(filterStudents(originalStudents, type));
          }}
        >
          {Object.entries(dropdownItems).map(([k, s]) => (
            <DropdownItem key={`dd-${k}`} eventKey={k}>
              {s}
            </DropdownItem>
          ))}
        </DropdownButton>
        
        繳回期限：
        <div style={{ flex: '0 0 25%' }} className="mr-2">
          <DateTimePicker
            containerClass="px-2 py-1"
            value={deadline}
            onChange={(date) => {
              setDeadline(date);
            }}
          />
        </div>
        <Button
          className="ml-auto"
          variant="success"
          disabled={!students.length}
          onClick={handleNoticePrint}
        >
          通知單列印
        </Button>
      </Row>
      <Row className="mb-2">
        <Form.Check
          className="mr-4"
          label="列印本學期視力表"
          checked={printCurrentSem}
          onChange={() => setPrintCurrentSem(!printCurrentSem)}
        />
        <Form.Check
          className="mr-4"
          label="列印歷年視力表"
          checked={printSightHistories}
          onChange={() => setPrintSightHistories(!printSightHistories)}
        />
      </Row>
      <Row>
        <EditableTableDiv
          headers={modalDisplayHeader}
          values={students}
          onSelected={(s) => {
            if (s.index !== undefined) {
              const v = students[s.index];
              v.selected = s.checked;
            } else {
              students.forEach((v) => {
                v.selected = s.checked;
              });
            }
            setStudent([...students]);
          }}
        />
      </Row>
    </>
  );
};

export const SightPDFPage = connector(WrapClassBaseLayout(sightPDFPage));
